import * as uuid from 'uuid';
import { z } from 'zod';

import { api, isDefined } from 'common';

import {
  DEFAULT_EDGE_PROPS,
  DEFAULT_EDGE_TYPE,
  ERROR_EDGE_TYPE,
} from './ReactFlow/Edges/constants';
import { type StartBlockConfig } from './ReactFlow/Nodes/Configs/Start/schema';
import {
  BaseReactFlowBlockDataSchema,
  FlowNodeType,
  ReactFlowBlockTypeDataSchema,
  ReactFlowPlaceholderDataSchema,
  isOutputBlockType,
  isSingletonBlockType,
  isStartBlockType,
} from './ReactFlow/types';

const BaseReactFlowNodeSchema = z.object({
  id: z.string(),
  type: z.enum(FlowNodeType),
  selected: z.boolean().optional(),
  position: z.object({ x: z.number(), y: z.number() }),
  measured: z
    .object({
      width: z.number().optional(),
      height: z.number().optional(),
    })
    .optional(),
  /**
   * TODO: Consider moving defined width and height to decorator nodes only.
   */
  width: z.number().optional(),
  height: z.number().optional(),
  parentId: z.string().optional(),
  extent: z.literal('parent').optional(),
  expandParent: z.boolean().optional(),
});
export type BaseReactFlowNode = z.infer<typeof BaseReactFlowNodeSchema>;

const BaseReactFlowBlockNodeSchema = BaseReactFlowNodeSchema.extend({
  data: BaseReactFlowBlockDataSchema,
});
export type BaseReactFlowBlockNode = z.infer<typeof BaseReactFlowBlockNodeSchema>;

const ReactFlowPlaceholderNodeSchema = BaseReactFlowBlockNodeSchema.extend({
  type: z.literal(FlowNodeType.Placeholder),
  data: ReactFlowPlaceholderDataSchema,
});
export type ReactFlowPlaceholderNode = z.infer<typeof ReactFlowPlaceholderNodeSchema>;

const ReactFlowBlockNodeSchema = BaseReactFlowBlockNodeSchema.extend({
  type: z.literal(FlowNodeType.Block),
  data: ReactFlowBlockTypeDataSchema,
});
export type ReactFlowBlockNode = z.infer<typeof ReactFlowBlockNodeSchema>;
/**
 * These types are used to represent a block node with only the id, type, and data properties.
 * They are used in contexts where the full properties of a block node are not needed.
 * By trimming down the properties, we can reduce the propagation of changes in areas where
 * the full block node properties are not required, such as in the block editor.
 * This is useful for performance, particularly since moving a node should not impact the block config.
 * Do not add `position`, `width`, or `height` or other visual properties to this type.
 */
export type PartialBaseReactFlowBlockNodeWithData = Pick<
  BaseReactFlowBlockNode,
  'id' | 'type' | 'data'
>;
export type PartialReactFlowBlockNodeWithData = Pick<ReactFlowBlockNode, 'id' | 'type' | 'data'>;

const ReactFlowStickyNoteNodeSchema = BaseReactFlowNodeSchema.extend({
  type: z.literal(FlowNodeType.StickyNote),
  data: api.FlowDefinition.StickyNoteDataSchema,
});
export type ReactFlowStickyNoteNode = z.infer<typeof ReactFlowStickyNoteNodeSchema>;

const ReactFlowGroupNodeSchema = BaseReactFlowNodeSchema.extend({
  type: z.literal(FlowNodeType.GroupNode),
  data: api.FlowDefinition.GroupNodeDataSchema,
});
export type ReactFlowGroupNode = z.infer<typeof ReactFlowGroupNodeSchema>;

export const ReactFlowNodeSchema = z.discriminatedUnion('type', [
  ReactFlowPlaceholderNodeSchema,
  ReactFlowBlockNodeSchema,
  ReactFlowStickyNoteNodeSchema,
  ReactFlowGroupNodeSchema,
]);
export type ReactFlowNode = z.infer<typeof ReactFlowNodeSchema>;

/**
 * This functionality is identical to `isPartialBlockNodeWithData`, but it is used to maintain the type guard for
 * the full `ReactFlowBlockNode` type, which includes all properties of a block node.
 *
 * Important, use this for nodes that are guaranteed to have all properties of `ReactFlowBlockNode`,
 * Be aware that this function will type cast a partial node to a full block node.
 */
export function isBlockNode(
  node?: Pick<BaseReactFlowNode, 'type' | 'position'> | null
): node is ReactFlowBlockNode {
  return node?.type === FlowNodeType.Block && isDefined(node?.position);
}

/**
 * This functionality is identical to `isBlockNode`, but it is used to maintain the type guard for
 * `PartialReactFlowBlockNodeWithData`, which is a subset of `ReactFlowBlockNode` that only includes
 * the `id`, `type`, and `data` properties.
 *
 * Important, use this for nodes that are not guaranteed to have all properties of `ReactFlowBlockNode`,
 * such as those used in the block editor where nodes are trimmed down.
 * In particular, do not add `position`, `width`, or `height` or other visual properties to this type.
 * This is useful for performance, particularly since moving a node should not impact the block config.
 */
export function isPartialBaseBlockNodeWithData(
  node?: Pick<ReactFlowNode, 'type'> | null
): node is PartialBaseReactFlowBlockNodeWithData {
  return node?.type === FlowNodeType.Block || node?.type === FlowNodeType.Placeholder;
}

/**
 * This functionality is identical to `isBlockNode`, but it is used to maintain the type guard for
 * `PartialReactFlowBlockNodeWithData`, which is a subset of `ReactFlowBlockNode` that only includes
 * the `id`, `type`, and `data` properties.
 *
 * Important, use this for nodes that are not guaranteed to have all properties of `ReactFlowBlockNode`,
 * such as those used in the block editor where nodes are trimmed down.
 * In particular, do not add `position`, `width`, or `height` or other visual properties to this type.
 * This is useful for performance, particularly since moving a node should not impact the block config.
 */
export function isPartialBlockNodeWithData(
  node?: Pick<ReactFlowNode, 'type'> | null
): node is PartialReactFlowBlockNodeWithData {
  return node?.type === FlowNodeType.Block;
}

export function isStickyNoteNode(node?: ReactFlowNode): node is ReactFlowStickyNoteNode {
  return node?.type === FlowNodeType.StickyNote;
}

export function isGroupNode(node?: ReactFlowNode): node is ReactFlowGroupNode {
  return node?.type === FlowNodeType.GroupNode;
}

export function isPlaceholderBlockNode(
  node?: Pick<ReactFlowNode, 'type'>
): node is ReactFlowPlaceholderNode {
  return node?.type === FlowNodeType.Placeholder;
}

export function isStartBlockNode(node?: ReactFlowNode): node is ReactFlowBlockNode & {
  data: api.FlowDefinition.StartBlock & { config: StartBlockConfig };
} {
  return isBlockNode(node) && isStartBlockType(node.data.uiBlockType);
}

export function isOutputBlockNode(
  node?: ReactFlowNode
): node is ReactFlowBlockNode & { data: api.FlowDefinition.OutputBlock } {
  return isBlockNode(node) && isOutputBlockType(node.data.uiBlockType);
}

export function isSingletonBlockNode(node?: ReactFlowNode): node is ReactFlowBlockNode & {
  data: api.FlowDefinition.StartBlockConfig | api.FlowDefinition.OutputBlock;
} {
  return isBlockNode(node) && isSingletonBlockType(node.data.uiBlockType);
}

const ReactFlowEdgeTypeSchema = z.enum([DEFAULT_EDGE_TYPE, ERROR_EDGE_TYPE]);
type ReactFlowEdgeType = z.infer<typeof ReactFlowEdgeTypeSchema>;

export enum EdgeHandleType {
  Default = 'Default',
  Error = 'Error',
}
export const ReactFlowErrorEdgeHandleName = '_____REACT_FLOW_ERROR_EDGE_HANDLE_____';

export function getSourceEdgeHandleType(sourceHandle: string | null | undefined): EdgeHandleType {
  return sourceHandle === ReactFlowErrorEdgeHandleName
    ? EdgeHandleType.Error
    : EdgeHandleType.Default;
}

export function getEdgeTypeForHandleType(edgeHandleType: EdgeHandleType): ReactFlowEdgeType {
  return edgeHandleType === EdgeHandleType.Default ? DEFAULT_EDGE_TYPE : ERROR_EDGE_TYPE;
}

export const ReactFlowEdgeSchema = z.object({
  id: z.string(),
  // The id of the source node
  source: z.string(),
  // sourceHandle is the condition or error handle name, such as 'true', 'false', or ReactFlowErrorEdgeHandleName
  sourceHandle: z.string().optional(),
  // The id of the target node
  target: z.string(),
  // targetHandle is currently always undefined (We always go to the next node's default input handle)
  targetHandle: z.string().optional(),
  data: z
    .object({
      label: z.string().optional(),
      isEdgeHovered: z.boolean().optional(),
      isNodeHovered: z.boolean().optional(),
    })
    .optional(),
  type: ReactFlowEdgeTypeSchema.optional(),
  style: z.any().optional(),
  markerEnd: z.any().optional(),
  edgeHandleType: z.enum(EdgeHandleType),
});
export type ReactFlowEdge = z.infer<typeof ReactFlowEdgeSchema>;

export function createReactFlowEdge(
  edgeProps: Pick<
    ReactFlowEdge,
    'source' | 'target' | 'sourceHandle' | 'targetHandle' | 'edgeHandleType'
  >
): ReactFlowEdge {
  const { edgeHandleType } = edgeProps;
  return {
    ...edgeProps,
    id: uuid.v4(),
    edgeHandleType,
    type: getEdgeTypeForHandleType(edgeHandleType),
    ...DEFAULT_EDGE_PROPS,
  };
}

export interface ReactFlowProps {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

export interface SearchBarOption {
  id: string;
  name: string;
}

export enum FlowCreationAction {
  CreateScratchFlow = 'CreateScratchFlow',
  CreateFromFlowTemplate = 'CreateFromFlowTemplate',
  CloneFromExistingFlow = 'CloneFromExistingFlow',
}
